import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PhotoIcon, DocumentIcon } from '@heroicons/react/24/outline';

const schema = yup.object({
  name: yup.string().required('Name is required'),
  father_name: yup.string(),
  mother_name: yup.string(),
  mobile: yup.string()
    .matches(/^[6-9]\d{9}$/, 'Invalid Indian mobile number')
    .required('Mobile number is required'),
  email: yup.string().email('Invalid email format'),
  address: yup.string(),
});

const AddCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleProfileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingProfile(true);
    const formData = new FormData();
    formData.append('profile_image', file);

    try {
      const response = await axios.post('http://localhost/saininewsite/backend/upload_profile.php', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setProfileImage({
          url: response.data.file_url,
          filename: response.data.filename
        });
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Error uploading profile image');
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingDocs(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents[]', file);
    });

    try {
      const response = await axios.post('http://localhost/saininewsite/backend/upload_docs.php', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setDocuments(prev => [...prev, ...response.data.files]);
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Error uploading documents');
    } finally {
      setUploadingDocs(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const customerData = {
        ...data,
        profile_image: profileImage?.url || '',
        documents: documents
      };

      const response = await axios.post('http://localhost/saininewsite/backend/add_customer.php', customerData);
      
      if (response.data.success) {
        alert('Customer added successfully!');
        reset();
        setProfileImage(null);
        setDocuments([]);
        navigate('/customers/view');
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Error adding customer';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Add New Customer
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name *
              </label>
              <input
                {...register('name')}
                type="text"
                className="input-field"
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mobile Number *
              </label>
              <input
                {...register('mobile')}
                type="tel"
                className="input-field"
                placeholder="Enter 10-digit mobile number"
              />
              {errors.mobile && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mobile.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Father's Name
              </label>
              <input
                {...register('father_name')}
                type="text"
                className="input-field"
                placeholder="Enter father's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mother's Name
              </label>
              <input
                {...register('mother_name')}
                type="text"
                className="input-field"
                placeholder="Enter mother's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                {...register('email')}
                type="email"
                className="input-field"
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className="input-field"
                placeholder="Enter complete address"
              />
            </div>
          </div>

          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {profileImage ? (
                  <img
                    src={profileImage.url}
                    alt="Profile"
                    className="h-20 w-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileUpload}
                  className="hidden"
                  id="profile-upload"
                />
                <label
                  htmlFor="profile-upload"
                  className="btn-secondary cursor-pointer"
                >
                  {uploadingProfile ? 'Uploading...' : 'Upload Photo'}
                </label>
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Documents
            </label>
            <div className="space-y-4">
              <div>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  id="document-upload"
                />
                <label
                  htmlFor="document-upload"
                  className="btn-secondary cursor-pointer"
                >
                  {uploadingDocs ? 'Uploading...' : 'Upload Documents'}
                </label>
              </div>
              
              {documents.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {documents.map((doc, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <DocumentIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-600 dark:text-gray-400 text-center truncate">
                        {doc.original_name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/customers/view')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              {loading ? 'Adding Customer...' : 'Add Customer'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddCustomer;
